import classNames from "classnames"
import React, { useEffect } from "react"

import TrackingLink from "src/components/TrackingLink"

import { useTracker } from "src/hooks/use_tracker"

export const TRANSIENT_MODULE_LINK =
  "https://marinas.dockwa.com/request-demo?utm_campaign=21875849-Leads%20Upgrade&utm_source=Product&utm_medium=Transient"
export const CONTRACTS_MODULE_LINK =
  "https://marinas.dockwa.com/request-demo?utm_campaign=21875849-Leads%20Upgrade&utm_source=Product&utm_medium=Contracts"
export const POS_MODULE_LINK =
  "https://marinas.dockwa.com/request-demo?utm_campaign=21875849-Leads%20Upgrade&utm_source=Product&utm_medium=FuelPOS"

const ModuleTiles = () => {
  const tracker = useTracker()

  useEffect(() => {
    tracker.trackEvent("leads_module_ctas:ctas_viewed")
  }, [])

  const modules = [
    {
      name: "Transient Module",
      link: TRANSIENT_MODULE_LINK,
      descriptionBold: "Turn missed calls into new revenue.",
      description:
        "Drive bookings 24/7, improve occupancy, and delight boaters with online booking, waitlist tools, and inventory management.",
      variant: "blue",
      icon: "fa-calendar-days",
    },
    {
      name: "Contracts Module",
      link: CONTRACTS_MODULE_LINK,
      descriptionBold: "Ditch the paperwork, get paid faster.",
      description:
        "Simplify slip contracts with digital agreements, automated billing, and flexible payment plans that customers love.",
      variant: "teal",
      icon: "fa-file-lines",
    },
    {
      name: "POS Module",
      link: POS_MODULE_LINK,
      descriptionBold: "One system, every sale.",
      description:
        "From the fuel dock to your ship store, streamline payments and grow revenue with an all-in-one POS, built for marinas.",
      variant: "orange",
      icon: "fa-cart-shopping",
    },
  ]

  const renderTileHeader = ({ icon, name, variant }) => (
    <div className="flex flex-row items-center justify-start space-x-4">
      <div
        className={classNames(
          "flex h-8 w-8 items-center justify-center rounded-lg",
          {
            "bg-blue-700 text-blue-50": variant === "blue",
            "bg-teal-700 text-teal-50": variant === "teal",
            "bg-orange-700 text-orange-100": variant === "orange",
          }
        )}
      >
        <i className={`fa-solid ${icon} text-xl`} />
      </div>
      <div
        aria-level="2"
        role="heading"
        className="text-xl font-bold leading-8"
      >
        {name}
      </div>
    </div>
  )

  const renderTileDescription = ({ descriptionBold, description }) => (
    <div>
      <strong>{descriptionBold}</strong> {description}
    </div>
  )

  const renderTileLink = ({ link, variant, name }) => (
    <TrackingLink
      href={link}
      newTab
      eventName="leads_module_ctas:cta_clicked"
      eventProperties={{
        module: name,
      }}
      className={classNames("btn btn-no-colors w-full font-bold text-white", {
        "bg-blue-700 hover:bg-blue-800 focus:border-blue-900 focus:bg-blue-800":
          variant === "blue",
        "bg-teal-700 hover:bg-teal-800 focus:border-teal-900 focus:bg-teal-800":
          variant === "teal",
        "bg-orange-800 hover:bg-orange-900 focus:border-orange-900 focus:bg-orange-900":
          variant === "orange",
      })}
    >
      Learn More
    </TrackingLink>
  )

  const renderTile = ({
    name,
    link,
    descriptionBold,
    description,
    variant,
    icon,
  }) => {
    const key = `${name.toLowerCase().replace(" ", "-")}-tile` // "Contracts Module" -> "contracts-module-tile"
    return (
      <div
        key={key}
        data-testid={key}
        className={classNames(
          "flex flex-1 flex-col justify-between rounded-lg border p-4 px-6 pt-6 shadow-md",
          {
            "border-blue-300 bg-blue-50 text-blue-900": variant === "blue",
            "border-teal-300 bg-teal-50 text-teal-900": variant === "teal",
            "border-orange-300 bg-orange-100 text-orange-900":
              variant === "orange",
          }
        )}
      >
        <div className="flex flex-col space-y-6 pb-6">
          {renderTileHeader({ icon, name, variant })}
          {renderTileDescription({ descriptionBold, description })}
        </div>

        {renderTileLink({ link, variant, name })}
      </div>
    )
  }

  return (
    <div
      data-testid="leads-module-tiles"
      className="flex w-full flex-col space-y-4 px-4 pb-8 lg:flex-row lg:space-x-4 lg:space-y-0 lg:px-0"
    >
      {modules.map(renderTile)}
    </div>
  )
}

export default ModuleTiles
